import { Injectable, computed, effect, signal } from '@angular/core';

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'wg-theme';
const DARK_CLASS = 'app-dark';

/**
 * Owns the app's colour theme. Persists the user's choice (system/light/dark)
 * to localStorage, toggles the `.app-dark` class on <html> (which drives both
 * our --wg-* tokens and PrimeNG's darkModeSelector), and tracks the OS
 * preference live while in "system" mode.
 *
 * An inline script in index.html applies the correct class before Angular
 * boots to avoid a flash of the wrong theme; this service keeps it in sync
 * afterwards.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** The user's selected mode. */
  readonly mode = signal<ThemeMode>(this.readStoredMode());

  /** Live OS preference (true = OS wants dark). */
  private readonly systemPrefersDark = signal<boolean>(this.mediaQuery?.matches ?? false);

  /** Whether dark is actually applied right now. */
  readonly isDark = computed(() =>
    this.mode() === 'dark' || (this.mode() === 'system' && this.systemPrefersDark())
  );

  private get mediaQuery(): MediaQueryList | null {
    return typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  }

  constructor() {
    // Keep the OS-preference signal current; only matters in "system" mode.
    this.mediaQuery?.addEventListener('change', (e) => this.systemPrefersDark.set(e.matches));

    // Apply class + persist whenever the resolved theme changes.
    effect(() => {
      const dark = this.isDark();
      if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle(DARK_CLASS, dark);
      }
    });

    effect(() => {
      try {
        localStorage.setItem(STORAGE_KEY, this.mode());
      } catch {
        /* storage unavailable (private mode / SSR) — non-fatal */
      }
    });
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
  }

  private readStoredMode(): ThemeMode {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === 'light' || v === 'dark' || v === 'system') return v;
    } catch {
      /* ignore */
    }
    return 'system';
  }
}
